'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';

const loginSchema = z.object({
  employee_id: z.string().min(1, 'Employee ID is required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const inputCls = 'w-full h-10 px-3.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/25 focus:border-[#D4AF37] transition-all';

export function LoginForm() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [redirectLink, setRedirectLink] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Detect ?expired=1 added by fetchAuth when a 401 triggers auto-logout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === '1') setSessionExpired(true);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (values: LoginFormValues) => {
    setServerError(null);
    setRedirectLink(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      const data = await res.json();
      if (!res.ok) {
        setServerError(data.message || 'Login failed. Please check your credentials.');
        if (data.redirect_link) setRedirectLink(String(data.redirect_link));
        return;
      }
      router.push('/chat');
      router.refresh();
    } catch {
      setServerError('Unable to connect. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {sessionExpired && (
        <div className="flex items-start gap-2.5 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>Your session has expired. Please sign in again.</span>
        </div>
      )}

      {serverError && (
        <div className="flex items-start gap-2.5 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            {serverError}
            {redirectLink && (
              <> <a href={redirectLink} target="_blank" rel="noopener noreferrer" className="underline font-semibold ml-1">Reset Password</a></>
            )}
          </span>
        </div>
      )}

      <div className="space-y-1.5">
        <label htmlFor="employee_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Employee ID
        </label>
        <input
          id="employee_id"
          type="text"
          autoComplete="username"
          placeholder="Enter your employee ID"
          className={`${inputCls} ${errors.employee_id ? 'border-red-400 focus:ring-red-400/25 focus:border-red-400' : ''}`}
          suppressHydrationWarning
          {...register('employee_id')}
        />
        {errors.employee_id && (
          <p className="text-xs text-red-600">{errors.employee_id.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="Enter your password"
            className={`${inputCls} pr-10 ${errors.password ? 'border-red-400 focus:ring-red-400/25 focus:border-red-400' : ''}`}
            suppressHydrationWarning
            {...register('password')}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.password && (
          <p className="text-xs text-red-600">{errors.password.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        suppressHydrationWarning
        className="w-full h-10 rounded-lg bg-[#D4AF37] hover:bg-[#C49A20] disabled:opacity-60 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 mt-2 shadow-sm shadow-[#D4AF37]/30"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Signing in…
          </>
        ) : (
          'Sign In'
        )}
      </button>
    </form>
  );
}
